import React from "react";

import { LengthInputSelector } from "@editor/components/editor/page/element-editor/components/modifiers/LengthInputModifier";

import DimensionFill from "@/svg/modifiers/direction/dimension-fill";
import DimensionFixed from "@/svg/modifiers/direction/dimension-fixed";
import DimensionHug from "@/svg/modifiers/direction/dimension-hug";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronDown, ChevronsUpDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";
import { exhaustiveSwitch, isNullish } from "replo-utils/lib/misc";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

const getDimensionOptions = (type: "width" | "height") => {
  return [
    {
      value: "auto" as const,
      label: "Hug",
      displayValue: "Hug",
      icon: (
        <DimensionHug
          className={twMerge("text-muted", type === "height" && "rotate-90")}
        />
      ),
    },
    {
      value: "full" as const,
      label: "Fill",
      displayValue: "Fill",
      icon: (
        <DimensionFill
          className={twMerge("text-muted", type === "height" && "rotate-90")}
        />
      ),
    },
    {
      value: "fixed" as const,
      label: "Fixed",
      displayValue: "Fixed",
      icon: (
        <DimensionFixed
          className={twMerge("text-muted", type === "width" && "rotate-90")}
        />
      ),
    },
  ];
};

type DimensionMode = "auto" | "full" | "fixed";

interface DimensionSelectorProps {
  type: "width" | "height";
  value: number | "auto" | "full" | null;
  onSelect: (value: number | "auto" | "full" | null) => void;
  placeholder: string;
}

const DimensionSelector: React.FC<DimensionSelectorProps> = ({
  type,
  value,
  onSelect,
  placeholder,
}) => {
  const parseDimensionValue = (inputValue: string): number => {
    const result = parseUnit(
      inputValue,
      { value: 0, unit: "px" },
      "default",
      "px",
    );
    if (typeof result.value === "number") {
      return result.value;
    }
    return Number.parseFloat(result.value);
  };
  const currentMode = (() => {
    if (value === "auto" || value === "full") {
      return value;
    }
    return "fixed";
  })();

  const [dimensionMode, setDimensionMode] = useOverridableState<DimensionMode>(
    currentMode,
    (newMode) => {
      onSelect(
        exhaustiveSwitch({ type: newMode })({
          auto: () => "auto" as "auto" | "full" | number,
          full: () => "full",
          fixed: () => parseDimensionValue(customValue),
        }),
      );
    },
  );

  const debouncedOnCustomValueChange = useDebouncedCallback(
    (newValue: string) =>
      exhaustiveSwitch({ type: dimensionMode })({
        fixed: () => onSelect(parseDimensionValue(newValue)),
        auto: undefined,
        full: undefined,
      }),
    300,
  );

  const [customValue, setCustomValue] = useOverridableState<string>(
    typeof value === "number" ? value.toString() : "100",
    debouncedOnCustomValueChange,
  );

  const handleDimensionModeChange = (dimensionMode: DimensionMode) => {
    // NOTE (Kevin 2025-08-18): Needed because if the mode changes during the debounced callback time, could have previous change override new mode
    debouncedOnCustomValueChange.cancel();
    setDimensionMode(dimensionMode);
  };

  const dimensionOptions = getDimensionOptions(type).map((option) => {
    return {
      value: option.value,
      displayValue: option.displayValue,
      label: (
        <MenuItem
          variant="default"
          size="sm"
          selected={dimensionMode === option.value}
          startEnhancer={option.icon}
        >
          {option.label}
        </MenuItem>
      ),
    };
  });

  const displayValue = isNullish(value)
    ? placeholder
    : exhaustiveSwitch({ type: dimensionMode })({
        auto: "Hug",
        full: "Fill",
        fixed: `${customValue}px`,
      });

  const selectedIcon = exhaustiveSwitch({ type: dimensionMode })({
    auto: (
      <DimensionHug
        className={twMerge("text-muted", type === "height" && "rotate-90")}
      />
    ),
    full: (
      <DimensionFill
        className={twMerge("text-muted", type === "height" && "rotate-90")}
      />
    ),
    fixed: (
      <DimensionFixed
        className={twMerge("text-muted", type === "width" && "rotate-90")}
      />
    ),
  });

  return (
    <div className="w-full overflow-hidden">
      {dimensionMode === "fixed" ? (
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <Combobox.Root
              options={dimensionOptions}
              value={dimensionMode}
              onChange={(newValue) =>
                handleDimensionModeChange(newValue as "auto" | "full" | "fixed")
              }
            >
              <Combobox.Trigger>
                <Combobox.SelectionButton
                  layoutClassName="w-full"
                  title="Fixed"
                  size="sm"
                  startEnhancer={selectedIcon}
                  titleAlignment="start"
                  endEnhancer={<ChevronDown size={12} className="text-muted" />}
                />
              </Combobox.Trigger>
              <Combobox.Popover align="center" layoutClassName="w-[140px]">
                <Combobox.Content
                  areOptionsSearchable={false}
                  emptySearchMessage="No matches found"
                />
              </Combobox.Popover>
            </Combobox.Root>
          </div>
          <div className="w-20">
            <LengthInputSelector
              // TODO (Noah, 2025-08-29): Weird to pass this in but the field isn't actually used for anything, we should remove it
              field="width"
              value={customValue}
              onChange={(newValue) => {
                const result = parseUnit(
                  newValue,
                  { value: 0, unit: "px" },
                  "default",
                  "px",
                );
                const numericValue =
                  typeof result.value === "number"
                    ? result.value.toString()
                    : result.value;
                setCustomValue(numericValue);
              }}
              placeholder="0px"
              metrics={["px"]}
              unitDefaults={{ value: "0", unit: "px" }}
              inputClassName="w-full"
              endEnhancer={() => (
                <ChevronsUpDown size={12} className="text-muted" />
              )}
            />
          </div>
        </div>
      ) : (
        <Combobox.Root
          options={dimensionOptions}
          value={dimensionMode}
          onChange={(newValue) =>
            handleDimensionModeChange(newValue as "auto" | "full" | "fixed")
          }
        >
          <Combobox.Trigger>
            <Combobox.SelectionButton
              layoutClassName="w-full"
              title={displayValue}
              isPlaceholder={!value}
              size="sm"
              startEnhancer={selectedIcon}
              titleAlignment="start"
              endEnhancer={<ChevronDown size={12} className="text-muted" />}
            />
          </Combobox.Trigger>
          <Combobox.Popover align="center" layoutClassName="w-[140px]">
            <Combobox.Content
              areOptionsSearchable={false}
              emptySearchMessage="No matches found"
            />
          </Combobox.Popover>
        </Combobox.Root>
      )}
    </div>
  );
};

export default DimensionSelector;
